import React from 'react';
import PropTypes from 'prop-types';
import { overviewGuide } from './caseGuides';
import { GuideContent } from './CaseGuidePanel';

// 점검 항목 키를 화면 제목으로 바꾼다. 모르는 키는 그대로 보여준다.
const CHECK_TITLES = {
    presentingGoalsAlign: '주 호소 ↔ 상담 목표',
    riskFollowUp: '위험 신호 → 목표의 확인 계획',
    changeTalkGoalsAlign: '변화대화 ↔ 상담 목표',
};

// 점검 결과 라벨을 화면 문구로 바꾼다.
const CHECK_LABELS = { aligned: '잘 이어짐', partial: '일부 이어짐', gap: '이어지지 않음', notApplicable: '해당 없음' };

// 연결 점검 본문(총평, 항목 간 점검, 보완할 연결). 마이페이지에서도 쓴다.
export function CoherenceBody({ data }) {
    return <>
        <p className="case-feedback-summary">{data.summary}</p>
        <ul className="case-check-list">{data.checks.map(check => <li key={check.key}>
            <div className="review-row"><h3>{CHECK_TITLES[check.key] || check.key}</h3><span className={`review-tag case-check-label ${check.label}`}>{CHECK_LABELS[check.label] || check.label}</span></div>
            <p>{check.note}</p>
        </li>)}</ul>
        {data.gaps.length > 0 && <><h3>보완할 연결</h3><ul className="case-guide-list improve">{data.gaps.map(gap => <li key={gap}>{gap}</li>)}</ul></>}
    </>;
}

// 연결 점검의 진행 상태와 결과.
function OverviewResult({ overview, outdated, onRetry }) {
    if (overview.status === 'loading') return <p className="ai-small" role="status">AI가 항목들이 서로 이어지는지 읽고 있습니다. 잠시만 기다려주세요.</p>;
    if (overview.status === 'error') return <div className="ai-alert" role="alert">{overview.error}{overview.retryable && <> <button type="button" onClick={onRetry}>다시 시도</button></>}</div>;
    return <>
        <CoherenceBody data={overview.data} />
        {outdated && <p className="ai-small case-outdated">점검 뒤 저장한 항목이 있습니다. 다시 점검을 받으면 최신 내용으로 확인합니다.</p>}
        <p className="ai-small">AI가 저장된 항목을 읽고 만든 학습용 점검입니다. 정답이나 평가 결과가 아닙니다.</p>
    </>;
}

// 전체 보기 탭의 오른쪽 패널. 점검 결과가 있으면 결과를 먼저, 가이드는 접어서 보여준다.
export default function CaseOverviewPanel({ overview, outdated, unsentLabels, onRetry }) {
    return <aside className="review-card case-guide" aria-label="연결 점검">
        <span className="ai-kicker">{overview ? 'AI CONNECTION CHECK' : 'GUIDE'}</span>
        <h2>전체 보기 · {overview ? '연결 점검' : '점검 가이드'}</h2>
        {unsentLabels.length > 0 && <p className="ai-alert">서버에 저장하지 않은 항목이 있습니다: {unsentLabels.join(', ')}. 연결 점검은 서버에 저장된 내용을 기준으로 합니다.</p>}
        <div aria-live="polite">{overview && <OverviewResult overview={overview} outdated={outdated} onRetry={onRetry} />}</div>
        {overview
            ? <details className="case-guide-more"><summary>점검 가이드 다시 보기</summary><GuideContent guide={overviewGuide} /></details>
            : <><GuideContent guide={overviewGuide} /><p className="ai-small case-guide-hint">연결 점검 받기를 누르면 이 자리에 AI 점검 결과가 나타납니다.</p></>}
    </aside>;
}

const coherenceShape = PropTypes.shape({
    summary: PropTypes.string,
    checks: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, label: PropTypes.string, note: PropTypes.string })),
    gaps: PropTypes.arrayOf(PropTypes.string),
});
const overviewShape = PropTypes.shape({
    status: PropTypes.oneOf(['loading', 'done', 'error']).isRequired,
    error: PropTypes.string,
    retryable: PropTypes.bool,
    data: coherenceShape,
});

CoherenceBody.propTypes = { data: coherenceShape.isRequired };

OverviewResult.propTypes = { overview: overviewShape.isRequired, outdated: PropTypes.bool.isRequired, onRetry: PropTypes.func.isRequired };
CaseOverviewPanel.propTypes = { overview: overviewShape, outdated: PropTypes.bool.isRequired, unsentLabels: PropTypes.arrayOf(PropTypes.string).isRequired, onRetry: PropTypes.func.isRequired };
